import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from "@aircall/ds";
import { AiAgentsFilled, ChevronDown, ChevronUp } from "@aircall/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiActionErrorCategory } from "@/graphql/generated/graphql";
import { AIActionItem } from "@/helpers/aiActions/aiActions.helpers.decl";
import { formatSecondsToTime } from "@/helpers/date/date.helpers";
import { TranscriptionAIActionParams } from "../TranscriptionAIActionParams/TranscriptionAIActionParams";

const MILLISECONDS_PER_SECOND = 1000;

const ERROR_CATEGORY_KEY: Record<AiActionErrorCategory, string> = {
  [AiActionErrorCategory.API_ERROR]: "ai_action.error.api_error",
  [AiActionErrorCategory.AUTH]: "ai_action.error.auth",
  [AiActionErrorCategory.NO_DATA]: "ai_action.error.no_data",
  [AiActionErrorCategory.TIMEOUT]: "ai_action.error.timeout",
  [AiActionErrorCategory.UNKNOWN]: "ai_action.error.unknown",
};

type TranscriptionAIActionItemProps = {
  action: AIActionItem;
};

function getSourceLabel({ applicationName, integrationName }: AIActionItem) {
  if (
    integrationName &&
    applicationName &&
    integrationName !== applicationName
  ) {
    return `${integrationName} (${applicationName})`;
  }
  return integrationName ?? applicationName;
}

function ActionStatus({ action }: TranscriptionAIActionItemProps) {
  const { t } = useTranslation();

  if (action.isSuccess) {
    return (
      <Badge color="green" tone="light">
        {t("ai_action.status.success")}
      </Badge>
    );
  }

  return (
    <Badge color="red" tone="light">
      {action.errorCategory
        ? t("ai_action.status.failure_with_reason", {
            reason: t(ERROR_CATEGORY_KEY[action.errorCategory]),
          })
        : t("ai_action.status.failure")}
    </Badge>
  );
}

function ActionDetails({ action }: TranscriptionAIActionItemProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1 rounded-b-md border border-t-0 border-border bg-accent p-4 text-sm">
      <div className="flex items-center justify-between gap-1">
        <span className="font-bold text-muted-foreground">
          {t("ai_action.details.title")}
        </span>
        <span className="text-muted-foreground">
          {formatSecondsToTime(action.startTime)}
        </span>
      </div>
      {action.durationMs != null && (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {t("ai_action.details.duration_label")}
          </span>
          <span className="text-muted-foreground">
            {t("ai_action.details.duration_value", {
              count: Math.round(action.durationMs / MILLISECONDS_PER_SECOND),
            })}
          </span>
        </div>
      )}
      {action.inputParams && (
        <TranscriptionAIActionParams
          label={t("ai_action.details.input_label")}
          rows={action.inputParams}
        />
      )}
      {action.outputParams && (
        <TranscriptionAIActionParams
          label={t("ai_action.details.output_label")}
          rows={action.outputParams}
        />
      )}
    </div>
  );
}

export function TranscriptionAIActionItem({
  action,
}: TranscriptionAIActionItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sourceLabel = getSourceLabel(action);
  const ChevronIcon = isOpen ? ChevronUp : ChevronDown;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      data-test="transcript-ai-action"
    >
      <CollapsibleTrigger
        className={cn(
          "flex min-h-18 w-full cursor-pointer items-center justify-between gap-4 border border-border bg-background p-4 text-left",
          isOpen ? "rounded-t-md" : "rounded-md"
        )}
        data-test="transcript-ai-action-trigger"
      >
        <span className="flex min-w-0 items-center gap-4">
          <Avatar className="size-7">
            {action.iconUrl && <AvatarImage src={action.iconUrl} alt="" />}
            <AvatarFallback
              className={action.isSuccess ? "bg-success" : "bg-destructive"}
            >
              <AiAgentsFilled className="size-4 text-primary-foreground" />
            </AvatarFallback>
          </Avatar>
          <span className="flex min-w-0 flex-col">
            <span className="text-base font-semibold text-foreground">
              {action.actionName}
            </span>
            {sourceLabel && (
              <span className="truncate text-xs text-muted-foreground">
                {sourceLabel}
              </span>
            )}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <ActionStatus action={action} />
          <ChevronIcon className="size-4 opacity-70" />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ActionDetails action={action} />
      </CollapsibleContent>
    </Collapsible>
  );
}
